import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone } from 'three/addons/utils/SkeletonUtils.js';

const loader = new GLTFLoader();
const assetCache = new Map();
const clipAliases = {
  idle: ['idle', 'stance'],
  walk: ['walk', 'run'],
  attack: ['attack', 'strike', 'punch'],
  rasengan: ['vortex', 'orb', 'power'],
  clones: ['echoes', 'summon', 'clone'],
  wind: ['signature', 'cast', 'spell'],
  sand: ['signature', 'cast', 'spell', 'attack'],
  guard: ['guard', 'block'],
  recharge: ['recharge', 'focus', 'channel'],
  hit: ['hit', 'impact', 'damage'],
};

function findClip(clips, name) {
  const aliases = clipAliases[name] || [name];
  for (const alias of aliases) {
    const exact = clips.find((clip) => clip.name.toLowerCase() === alias);
    if (exact) return exact;
    const partial = clips.find((clip) => clip.name.toLowerCase().includes(alias) && !clip.name.toLowerCase().includes('idle'));
    if (partial) return partial;
  }
  const fallback = { rasengan: 'punch', clones: 'punch', wind: 'attack', sand: 'attack', guard: 'attacking_idle', recharge: 'idle' }[name];
  return fallback ? clips.find((clip) => clip.name.toLowerCase().includes(fallback)) : null;
}

export async function attachRiggedModel(host, specification) {
  if (!specification?.url) return null;
  detachRiggedModel(host);
  if (!assetCache.has(specification.url)) assetCache.set(specification.url, loader.loadAsync(specification.url));
  const gltf = await assetCache.get(specification.url);
  const root = clone(gltf.scene);
  root.scale.setScalar(specification.scale || 1);
  root.position.fromArray(specification.position || [0, 0, 0]);
  root.rotation.y = specification.rotationY || 0;
  root.traverse((object) => {
    if (!object.isMesh) return;
    object.castShadow = true;
    object.receiveShadow = true;
  });
  host.add(root);
  if (host.userData.model) host.userData.model.visible = false;
  const mixer = new THREE.AnimationMixer(root);
  const actions = Object.fromEntries(Object.keys(clipAliases).map((name) => {
    const clip = findClip(gltf.animations, name);
    return [name, clip ? mixer.clipAction(clip) : null];
  }));
  const rig = { root, mixer, actions, active: null };
  host.userData.rig = rig;
  playRiggedAnimation(host, 'idle', 0);
  return rig;
}

export function detachRiggedModel(host) {
  const rig = host?.userData?.rig;
  if (!rig) return;
  rig.mixer.stopAllAction();
  host.remove(rig.root);
  delete host.userData.rig;
  if (host.userData.model) host.userData.model.visible = true;
}

export function playRiggedAnimation(host, name, fade = .16, loop = true) {
  const rig = host?.userData?.rig;
  const next = rig?.actions?.[name];
  if (!rig || !next || rig.active === next) return false;
  next.enabled = true;
  next.reset();
  next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
  next.clampWhenFinished = !loop;
  next.fadeIn(fade).play();
  rig.active?.fadeOut(fade);
  rig.active = next;
  return true;
}

export function updateRiggedModel(host, delta) {
  host?.userData?.rig?.mixer.update(delta);
}
